import { Component } from '@theme/component';

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  #storage_key = 'announcement-bar-closed';

  /** @type {IntersectionObserver | null} */
  #intersectionObserver = null;

  #lastScrollTop = 0;

  /** @type {number | null} */
  #timeout = null;

  #animationDelay = 150;

  connectedCallback() {
    if (sessionStorage.getItem(this.#storage_key)) {
      this.parentElement?.classList.add('hidden');
      this.remove();

      return;
    }

    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    this.parentElement?.querySelector('.js-announcement-bar-close')?.addEventListener('click', () => this.close());

    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    if (this.getAttribute('sticky') === 'always') {
      this.#observeStickyPosition();
      document.addEventListener('scroll', this.#handleWindowScroll);
    }

    this.play();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#intersectionObserver?.disconnect();
    document.removeEventListener('scroll', this.#handleWindowScroll);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  close() {
    sessionStorage.setItem(this.#storage_key, 'true');

    this.suspend();
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    this.parentElement?.classList.add('hidden');
    this.remove();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    this.#current = current;

    let relativeIndex = current % (this.refs.slides ?? []).length;
    if (relativeIndex < 0) {
      relativeIndex += (this.refs.slides ?? []).length;
    }

    this.refs.slides?.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${index !== relativeIndex}`);
    });
  }

  #observeStickyPosition() {
    if (this.#intersectionObserver) return;

    this.#intersectionObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        this.dataset.stickyState = entry.isIntersecting ? 'inactive' : 'active';
      },
      { threshold: 1 }
    );

    this.#intersectionObserver.observe(this);
  }

  #handleWindowScroll = () => {
    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const isScrollingUp = scrollTop < this.#lastScrollTop;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    const isAtTop = this.getBoundingClientRect().top >= 0;

    if (isAtTop) {
      this.dataset.scrollDirection = 'none';
    } else if (isScrollingUp) {
      this.dataset.scrollDirection = 'up';
    } else {
      this.dataset.scrollDirection = 'down';
    }

    this.#lastScrollTop = scrollTop;
  };

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
